// Where telemetry goes and what it calls itself.
//
// Read off `<html>`'s data attributes rather than baked in at build time: the shell
// is built once and deployed to several domains/stages, and `data-medialib-env` is
// already how index.html declares which one it is. A deployment can rewrite those
// attributes without a rebuild; the defaults below are what `just dev` runs on.

import { htmlData } from '../htmlData'

/**
 * The Faro collector endpoint: our own self-hosted Grafana Alloy `faro.receiver`.
 *
 * First-party infrastructure (a medialib.tv host we run), not a SaaS collector — so
 * no visitor data leaves our control and the GDPR posture is the same one the content
 * API already has. It is nonetheless a *different origin* from the shell, which has
 * two consequences the Alloy side must satisfy:
 *
 *  - the receiver must send `Access-Control-Allow-Origin` for each shell origin, or
 *    every beacon fails the preflight (silently — Faro does not surface transport
 *    errors to the app);
 *  - it must accept the `traceparent`/`tracestate` request headers in
 *    `Access-Control-Allow-Headers`, since the tracing instrumentation is on.
 *
 * A deployment that proxies Alloy onto its own origin can point this at a
 * root-relative path instead, via `<html data-medialib-collector="/collect">` — that
 * drops the preflight entirely and is the better setup where it is available.
 */
export const COLLECTOR_URL = htmlData('medialib-collector', 'https://faro.trichechus.medialib.tv/collect')

/** Which deployment this is (`dev`, `staging`, `production`) — Faro's `environment`. */
export const ENVIRONMENT = htmlData('medialib-env', 'dev')

/** The app name every signal is tagged with at the collector. */
export const APP_NAME = 'medialib-ui'

/**
 * The shell's version, for release-over-release comparison in Grafana. Sourced from
 * `<html data-medialib-version>` — the CI job that publishes the shell stamps the
 * package version onto index.html; a local `just dev` build has none and reports
 * `dev`.
 */
export const APP_VERSION = htmlData('medialib-version', 'dev')

/**
 * Hosts the tracing instrumentation may attach trace-context headers to.
 *
 * `traceparent`/`tracestate` on a cross-origin request triggers a CORS preflight the
 * third party will reject, so propagation is limited to our own origin — which is
 * where the content API lives anyway. The player's HLS segments come from
 * `player.next.medialib.tv` and Bunny, and are deliberately NOT in this list:
 * they're instrumented through the player's own quality events instead (see the
 * watch pilet), not through OTel spans.
 */
export const TRACE_PROPAGATE_TO: Array<string | RegExp> = [
  typeof window === 'undefined' ? '' : window.location.origin,
]

/**
 * Whether telemetry should run at all.
 *
 * Off when the collector is unreachable by construction — a `file://` load, a test
 * harness, or SSR — so nothing beacons into the void. Not a consent check: that is
 * a separate axis, see {@link enableRumTelemetry}.
 */
export function isTelemetrySupported (): boolean {
  return typeof window !== 'undefined' && window.location.protocol.indexOf('http') === 0
}
